import React, { forwardRef, useEffect, useState } from 'react';
import { GoChevronLeft, GoChevronRight, GoPrimitiveDot } from 'react-icons/go';
import { Translate } from 'react-localize-redux';
import Swipeable from 'react-swipeable';
import { CSSTransition, SwitchTransition } from 'react-transition-group';
import styled, { css } from 'styled-components';

import Heading, { SubHeading } from '../../components/heading';
import * as images from './character-images';
import Section from './section';

const gameCharacterMap = {
  '0': ['sarah', 'leader', 'offender'],
  '1': ['sarah', 'leader', 'anego', 'branchManager'],
  '2': ['sarah', 'leader', 'offender'],
  '3': [], // PSY
  '6': [],  // Compass
  '7': [],  // Compass
  '8': []  // Compass
};

const characterTrailers = {
  sarah: 'https://www.youtube.com/embed/qLTQp8ZAPp0',
  leader: 'https://www.youtube.com/embed/McbQK3PRag4',
  offender: 'https://www.youtube.com/embed/VDgmQV45Odk'
};

const Background = styled.div`
  background: ${props => props.theme.primary};
  overflow: hidden;
  position: relative;
`;

const DotContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 2em;
`;

const Dot = styled(GoPrimitiveDot)`
  color: rgba(255, 255, 255, 0.5);
  margin: 0 0.5em;

  ${props =>
    props.active
      ? css`
          color: #fff;
        `
      : css`
          cursor: pointer;
        `}
`;

const Content = styled(Swipeable)`
  position: relative;

  @media (min-width: 768px) {
    padding: 0 1.5em;
  }
`;

const Button = styled.div`
  cursor: pointer;
  font-size: 2.5em;
  color: #111111;
  position: absolute;
  width: 1em;
  height: 1em;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1;
  top: 8em;

  @media (min-width: 768px) {
    width: 2em;
    height: 2em;
    top: 6em;
  }
`;

const Next = styled(Button)`
  right: -0.5em;
`;

const Previous = styled(Button)`
  left: -0.5em;
`;

const CharacterContainer = styled.div`
  display: grid;
  grid-template-columns: minmax(9em, 1fr) minmax(auto, 20em);
  grid-template-rows: auto 1fr auto;
  grid-template-areas:
    'phrase  phrase'
    'image   data'
    'trailer trailer';
  margin-top: 1.5em;
  line-height: 1.5;

  @media (min-width: 576px) {
    grid-template-areas:
      'phrase phrase'
      'image  data'
      '.      trailer';
  }

  @media (min-width: 768px) {
    grid-template-columns: 16em 1fr;
    grid-template-areas:
      'image phrase'
      'image data'
      'image trailer';
  }

  &.enter {
    opacity: 0;
  }

  &.enter-active {
    opacity: 1;
    transition: 350ms ease;
  }

  &.exit {
    opacity: 0;
    transition: 350ms ease;
  }
`;

const CharacterBackground = styled.img`
  display: block;
  position: absolute;
  top: 0;
  height: 100%;

  @media (min-width: 576px) {
    left: 33.3%;
  }

  &.enter {
    opacity: 0;
  }

  &.enter-active {
    opacity: 1;
    transition: 350ms ease;
  }

  &.exit {
    opacity: 0;
    transition: 350ms ease;
  }
`;

const CharacterPhrase = styled.div`
  grid-area: phrase;
  background: #111111;
  font-size: 1.2em;
  font-weight: bold;
  text-align: center;
  padding: 0.5em 1em;

  > span {
    display: inline-block;
  }
`;

const CharacterImageContainer = styled.div`
  grid-area: image;
  position: relative;

  @media (min-width: 768px) {
    min-height: 35em;
  }
`;

const CharacterImage = styled.img`
  display: block;
  position: absolute;
  min-width: 16em;
  width: 100%;
  top: 1em;
  left: 50%;
  transform: translateX(-50%);
`;

const CharacterData = styled.div`
  grid-area: data;
  position: relative;
`;

const CharacterNameContainer = styled.div`
  @media (min-width: 768px) {
    display: flex;
    align-items: center;
    margin-top: 1.5em;

    > *:not(:first-of-type) {
      margin-left: 1em;
    }
  }
`;

const CharacterName = styled.div`
  font-size: 2.5em;
  font-weight: bold;
  margin-top: 0.25em;

  @media (min-width: 768px) {
    margin-top: 0;
  }
`;

const CharacterVoice = styled.div`
  font-size: 1.1em;
  font-weight: bold;
  margin-top: 0.25em;
  text-align: center;

  @media (min-width: 375px) {
    display: flex;
  }

  @media (min-width: 768px) {
    margin-top: 0;
  }

  > div {
    background: white;
    color: ${props => props.theme.primary};
    padding: 0.4em;

    @media (min-width: 375px) {
      padding-left: 1.6em;
      padding-right: 1.6em;
    }

    @media (min-width: 768px) {
      padding-left: 1em;
      padding-right: 1em;
    }
  }
`;

const CharacterBio = styled.div`
  margin-top: 1.5em;
`;

const CharacterTable = styled.div`
  display: grid;
  align-items: center;
  grid-gap: 0 0.75em;
  margin-top: 1.5em;

  @media (min-width: 375px) {
    grid-template-columns: auto 1fr;
  }
`;

const CharacterTableKey = styled.div`
  background: white;
  color: ${props => props.theme.primary};
  font-size: 0.8em;
  font-weight: bold;
  margin: 0.75em 0 0.5em;
  padding: 0.4em 1em;
  text-align: center;

  @media (min-width: 375px) {
    margin: 0.5em 0;
  }
`;

const CharacterTableValue = styled.div`
  font-size: 0.8em;
`;

const CharacterTrailer = styled.div`
  grid-area: trailer;
  margin-top: 2em;
  position: relative;

  &::after {
    padding-top: 56.25%;
    display: block;
    content: '';
  }

  > iframe {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
  }
`;

const CharacterTrailerDummy = styled.div`
  grid-area: trailer;
  height: 5em;
  width: 100%;
`;

const Character = ({ character, image }) => (
  <CharacterContainer>
    <CharacterPhrase>
      <span>
        <Translate id={`landingPage.characters.${character}.phrase.1`} />
      </span>
      <span>
        <Translate id={`landingPage.characters.${character}.phrase.2`} />
      </span>
    </CharacterPhrase>
    <CharacterImageContainer>
      <CharacterImage src={image.body} />
    </CharacterImageContainer>
    <CharacterData>
      <CharacterNameContainer>
        <CharacterName>
          <Translate id={`landingPage.characters.${character}.name`} />
        </CharacterName>
        <CharacterVoice>
          <div>
            <Translate id={`landingPage.characters.keys.voice`} />
            <Translate id={`landingPage.characters.${character}.voice`} />
          </div>
        </CharacterVoice>
      </CharacterNameContainer>
      <CharacterBio>
        <Translate id={`landingPage.characters.${character}.bio`} />
      </CharacterBio>
      <CharacterTable>
        <CharacterTableKey>
          <Translate id={`landingPage.characters.keys.age`} />
        </CharacterTableKey>
        <CharacterTableValue>
          <Translate id={`landingPage.characters.${character}.age`} />
        </CharacterTableValue>
        <CharacterTableKey>
          <Translate id={`landingPage.characters.keys.favorite`} />
        </CharacterTableKey>
        <CharacterTableValue>
          <Translate id={`landingPage.characters.${character}.favorite`} />
        </CharacterTableValue>
        <CharacterTableKey>
          <Translate id={`landingPage.characters.keys.job`} />
        </CharacterTableKey>
        <CharacterTableValue>
          <Translate id={`landingPage.characters.${character}.job`} />
        </CharacterTableValue>
        <CharacterTableKey>
          <Translate id={`landingPage.characters.keys.skill`} />
        </CharacterTableKey>
        <CharacterTableValue>
          <Translate id={`landingPage.characters.${character}.skill`} />
        </CharacterTableValue>
      </CharacterTable>
    </CharacterData>
    {characterTrailers[character] ? (
      <CharacterTrailer>
        <iframe
          allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          frameBorder="0"
          loading="lazy"
          src={characterTrailers[character]}
          title={`${character} trailer`}
        />
      </CharacterTrailer>
    ) : (
      <CharacterTrailerDummy />
    )}
  </CharacterContainer>
);

const Characters = forwardRef(({ gameId }, ref) => {
  const characters = gameCharacterMap[gameId];
  const [index, setIndex] = useState(0);
  const incrementIndex = () => setIndex((index + 1) % characters.length);
  const decrementIndex = () =>
    setIndex((index - 1 + characters.length) % characters.length);

  useEffect(() => setIndex(0), [gameId]);

  // eslint-disable-next-line import/namespace
  const image = images[characters[index]] || {};

  return (
    <Background>
      <SwitchTransition>
        <CSSTransition key={index} timeout={350}>
          <CharacterBackground src={image.background} />
        </CSSTransition>
      </SwitchTransition>
      <Section ref={ref} style={{ position: 'relative' }}>
        <Heading>CHARACTERS</Heading>
        <SubHeading>
          <Translate id="landingPage.characters.subtitle" />
        </SubHeading>
        <DotContainer>
          {characters.map((char, i) => (
            <Dot
              active={i === index ? 1 : 0}
              key={char}
              onClick={() => setIndex(i)}
              size="1.5em"
            />
          ))}
        </DotContainer>
        <Content onSwipedLeft={incrementIndex} onSwipedRight={decrementIndex}>
          <Previous onClick={decrementIndex}>
            <GoChevronLeft size="100%" />
          </Previous>
          <Next onClick={incrementIndex}>
            <GoChevronRight size="100%" />
          </Next>
          <SwitchTransition>
            <CSSTransition key={index} timeout={350}>
              <Character character={characters[index]} image={image} />
            </CSSTransition>
          </SwitchTransition>
        </Content>
      </Section>
    </Background>
  );
});

export default Characters;
